import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppFloat from "@/components/feature/WhatsAppFloat";
import AveriasFrecuentesSection from "@/components/feature/AveriasFrecuentesSection";
import FAQSection from "@/components/feature/FAQSection";
import ComoTrabajamosSection from "@/components/feature/ComoTrabajamosSection";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PricingSection from "@/components/feature/PricingSection";
import TrustBannerSection from "@/components/feature/TrustBannerSection";
import { preciosPorServicio } from "@/mocks/precios";

export default function MotorizacionPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="Motorización de Persianas Málaga | Persianas Málaga"
        description="Motorización de persianas en Málaga sin obras. Motores Somfy y Nice con mando. Instalación en 1 hora. Presupuesto gratis, 2 años de garantía."
      />
      <Navbar />

      <Breadcrumb items={[
        { label: "Inicio", path: "/" },
        { label: "Servicios" },
        { label: "Motorización" },
      ]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Motorización de Persianas en Málaga",
        "provider": { "@type": "LocalBusiness", "name": "Persianas Málaga", "telephone": "+34 951 12 07 70" },
        "areaServed": "Málaga capital",
        "description": "Motorización de persianas en Málaga sin obras. Motores Somfy y Nice con mando a distancia. Instalación en 1 hora. Presupuesto gratis.",
        "serviceType": "Motorización de Persianas",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Servicios de motorización de persianas",
          "itemListElement": [
            { "@type": "Offer", "name": "Motorización sin obras", "price": "120", "priceCurrency": "EUR" },
            { "@type": "Offer", "name": "Cambio de motor", "price": "120", "priceCurrency": "EUR" },
            { "@type": "Offer", "name": "Revisión y mantenimiento", "price": "30", "priceCurrency": "EUR" }
          ]
        }
      }) }} />

      <section className="py-14 md:py-20 bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="flex-1 w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight font-heading">
                Motorización de persianas <span className="text-primary-500">en Málaga</span>
              </h1>
              <p className="text-sm md:text-lg text-foreground-600 mb-6 max-w-lg leading-relaxed">
                Automatiza tus persianas sin obras. Motores Somfy y Nice con mando a distancia. Instalación en 1 hora. Servicio en los 16 barrios de Málaga. Presupuesto gratis, 2 años de garantía.
              </p>
              <a href="tel:+34951120770" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-background-50 font-bold text-sm md:text-base px-8 py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                951 12 07 70
              </a>
            </div>
            <div className="w-full lg:w-[45%] h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden flex-shrink-0">
              <img alt="Motorización de persianas con mando a distancia en Málaga" className="w-full h-full object-cover object-top" loading="eager" src="/images/001-serv-motor-hero-v3.webp" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 font-heading text-center">Ventajas de motorizar tus persianas</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: "ri-smartphone-line", title: "Control por mando", desc: "Sube y baja todas las persianas con un solo mando. Programación horaria para abrir y cerrar automáticamente al amanecer y anochecer." },
              { icon: "ri-home-smile-line", title: "Sin obras", desc: "La instalación se hace en menos de 1 hora. Aprovechamos el cajetín y los soportes existentes. Sin polvo, sin escombros, sin complicaciones." },
              { icon: "ri-shield-flash-line", title: "Seguridad", desc: "Simula presencia en casa cuando estás fuera. Las persianas se abren y cierran según un horario programado. Disuasión eficaz contra robos." },
              { icon: "ri-sun-line", title: "Ahorro energético", desc: "Baja las persianas en verano para mantener la casa fresca. Súbelas en invierno para aprovechar el calor del sol. Reduce tu factura." },
              { icon: "ri-volume-down-line", title: "Silencioso", desc: "Motores tubulares de última generación. Funcionamiento silencioso que no molesta. Ideal para hogares con bebés o trabajo desde casa." },
              { icon: "ri-cloud-line", title: "Domótica integrada", desc: "Compatible con Alexa, Google Home y sistemas domóticos. Controla tus persianas desde el móvil estés donde estés." },
            ].map((item) => (
              <div key={item.title} className="p-5 md:p-6 bg-background-50 rounded-2xl border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-900/10 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center"><i className={`${item.icon} text-secondary-800 text-lg`}></i></span>
                </div>
                <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AveriasFrecuentesSection ids={["motorizar", "motor-no-funciona", "mando-no-responde", "no-sube-ni-baja"]} />

      <FAQSection
        title="Preguntas frecuentes sobre motorización"
        questions={[
          {
            question: "¿Cuánto cuesta motorizar una persiana?",
            answer: "Motorizar una persiana cuesta entre 160€ y 320€ por persiana, dependiendo del tipo de motor. Motor básico con mando: desde 160€. Motor con mando y programación horaria: desde 220€. Motor con domótica (Alexa/Google Home): desde 280€. Motores Somfy (la marca líder): desde 240€. El precio incluye el motor, el mando, la instalación y la configuración. Presupuesto exacto y gratis tras revisar tu cajetín.",
          },
          {
            question: "¿Se necesita hacer obra para poner un motor en una persiana?",
            answer: "No. En la mayoría de las persianas, el motor se instala dentro del tubo de enrollamiento existente, dentro del cajetín. No hay que romper nada, no hay polvo y no se modifica la fachada. La instalación dura entre 45 minutos y 1 hora por persiana. Solo en casos muy raros (cajetín demasiado pequeño o sistema muy antiguo) hace falta una pequeña adaptación, que te explicamos antes de hacer nada.",
          },
          {
            question: "¿Los motores consumen mucha luz?",
            answer: "No. Un motor tubular consume entre 120 y 180 vatios durante los 30 segundos que tarda en subir o bajar la persiana. Para que te hagas una idea, motorizar una persiana y usarla dos veces al día consume menos de 3€ al año en electricidad. Es insignificante comparado con el ahorro energético que proporciona al poder bajar las persianas fácilmente en verano.",
          },
          {
            question: "¿Qué marca de motor es mejor, Somfy o Nice?",
            answer: "Las dos son excelentes y líderes del mercado. Somfy es la más extendida en España y tiene una gama muy amplia con buena integración domótica. Nice ofrece motores muy silenciosos y una excelente relación calidad-precio. Instalamos ambas y te recomendamos la más adecuada según tu instalación, tu presupuesto y si quieres domótica o no. Con cualquiera de las dos tienes 2 años de garantía.",
          },
          {
            question: "¿Se puede controlar la persiana desde el móvil?",
            answer: "Sí, completamente. Instalamos motores con conectividad por radiofrecuencia que se vinculan a un puente domótico (TaHoma de Somfy o Nice Home). Desde la app gratuita controlas todas las persianas de tu casa, creas horarios, simulas presencia y lo integras con Alexa o Google Home. También puedes controlarlas desde fuera de casa — ideal para bajar las persianas si ves que se acerca una tormenta.",
          },
        ]}
      />

      <ComoTrabajamosSection />

      <section className="py-10 md:py-14 bg-gradient-to-br from-background-50 via-primary-50/15 to-accent-50/20">
        <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">Motores que instalamos</h2>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {["Somfy", "Nice", "Cherubini", "Mitto", "Maco", "Ribind"].map((m) => (
              <span key={m} className="bg-background-100 text-foreground-700 font-semibold text-sm px-5 py-2.5 rounded-full border border-background-200/60">{m}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3 font-heading">Moderniza tus persianas hoy</h3>
          <p className="text-sm md:text-base text-background-50/80 mb-6">Presupuesto gratis · Instalación en 1 hora · 2 años de garantía</p>
          <a href="tel:+34951120770" className="inline-flex items-center justify-center gap-2 bg-background-50 hover:bg-background-100 text-primary-600 font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-base"></i></span>
            951 12 07 70
          </a>
        </div>
      </section>

      <PricingSection ids={preciosPorServicio["motorizacion"]} />

      <TrustBannerSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}